/**
 * O que pode ser enviado, e como cada arquivo é classificado.
 *
 * ## Por que a regra deixou de ser só magic bytes
 *
 * Até aqui o sistema aceitava seis tipos e conferia todos pelos bytes iniciais.
 * A regra funcionava porque os seis eram formatos que o navegador **renderiza**
 * — e um `.jpg` com HTML dentro vira XSS quando servido pelo CDN.
 *
 * Projeto de obra não é isso. `.dwg`, `.rvt`, `.ifc`, `.step` e os demais são
 * formatos que o navegador **baixa**, nunca executa. Conferir assinatura de
 * quarenta e quatro formatos daria uma tabela que envelhece a cada versão do
 * AutoCAD e reprovaria arquivo legítimo — e não protegeria de nada, porque o
 * risco nunca esteve no conteúdo desses arquivos.
 *
 * A regra passa a ser em três camadas:
 *
 * 1. **Imagem e PDF continuam conferidos pelos bytes.** São os que o navegador
 *    abre na própria janela: uma imagem entra em `<img>`, um PDF tem leitor
 *    embutido e pode conter JavaScript.
 * 2. **O resto é liberado por extensão**, dentro do que o órgão configurou, e
 *    guardado como `application/octet-stream`. Servido assim, um HTML renomeado
 *    para `.dwg` é baixado, não executado.
 * 3. **{@link FORBIDDEN_UPLOAD_EXTENSIONS} não entra de jeito nenhum**, nem que
 *    o gestor escreva na lista. É o piso que a tela de configurações não
 *    alcança.
 */

// ───────────────────────────────────────────────── extensões padrão

/**
 * O que um órgão de obras públicas troca no dia a dia.
 *
 * Projeto e modelo (CAD, BIM, 3D), levantamento, documento, planilha e pacote
 * compactado. É a lista de partida: a configuração do órgão acrescenta, retira
 * ou substitui.
 */
export const DEFAULT_UPLOAD_EXTENSIONS = [
  // desenho e modelagem
  'dwg',
  'dxf',
  'rvt',
  'rfa',
  'rte',
  'skp',
  'nwd',
  'nwc',
  'nwf',
  'ifc',
  'dgn',
  '3dm',
  'pln',
  'sldprt',
  'sldasm',
  'slddrw',
  'ipt',
  'iam',
  'idw',
  'step',
  'stp',
  'iges',
  'igs',
  'stl',
  'obj',
  'fbx',
  // levantamento
  'las',
  'laz',
  // documentos
  'pdf',
  'doc',
  'docx',
  'txt',
  'rtf',
  'ppt',
  'pptx',
  // planilhas
  'xlsx',
  'xls',
  'csv',
  // compactados
  'zip',
  'rar',
  '7z',
  'tar',
  'gz',
  'tgz',
] as const;

/**
 * O piso que nenhuma configuração derruba.
 *
 * Dois motivos distintos. `html`, `htm`, `svg` e `xhtml` são renderizados pelo
 * navegador e executam script — servi-los de um domínio do município é entregar
 * XSS com o selo da prefeitura. `exe`, `msi`, `bat` e companhia transformariam
 * o CDN do órgão em ponto de distribuição de executável.
 *
 * Um gestor pode ampliar a lista de aceitos; não pode ampliar até aqui.
 */
export const FORBIDDEN_UPLOAD_EXTENSIONS = [
  'html',
  'htm',
  'xhtml',
  'shtml',
  'svg',
  'xml',
  'js',
  'mjs',
  'cjs',
  'wasm',
  'exe',
  'dll',
  'msi',
  'bat',
  'cmd',
  'com',
  'scr',
  'sh',
  'bash',
  'ps1',
  'vbs',
  'jar',
  'apk',
  'app',
  'deb',
  'rpm',
] as const;

/**
 * Sempre aceitos, mesmo em lista branca.
 *
 * A galeria da obra e a prova fotográfica do relatório dependem deles. Deixar
 * que uma configuração os removesse faria o envio de foto parar de funcionar
 * sem que a tela de configurações dissesse isso a ninguém.
 */
export const ALWAYS_ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'] as const;

/**
 * O que o navegador abre na própria janela, e o tipo que os bytes precisam
 * confirmar.
 *
 * Esta lista decide duas coisas ao mesmo tempo, e é por isso que ela vive aqui
 * e não em cada uma delas:
 *
 * 1. **No envio**, é a única lista ainda conferida byte a byte. Imagem entra em
 *    `<img>` e PDF tem leitor embutido que executa JavaScript — um HTML
 *    disfarçado de `.png` viraria XSS servido pelo CDN do município. Todo o
 *    resto é guardado como `application/octet-stream`, que o navegador baixa em
 *    vez de executar.
 * 2. **Na tela**, é quem ganha o ícone de olho. Oferecer "visualizar" num
 *    `.dwg` abriria uma aba que baixa o arquivo assim mesmo, o que faz o botão
 *    mentir.
 *
 * Separar as duas listas deixaria o olho aparecer em arquivo que a aba não
 * mostra, ou sumir de um que ela mostra. É a mesma pergunta: o navegador
 * renderiza isto?
 */
export const RENDERED_MIME_BY_EXTENSION: Readonly<Record<string, string>> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  pdf: 'application/pdf',
};

/** O arquivo abre numa aba, ou só baixa? */
export function isViewableInBrowser(extension: string): boolean {
  return extension.toLowerCase() in RENDERED_MIME_BY_EXTENSION;
}

// ───────────────────────────────────────────────────── classificação

export type FileKind = 'projeto' | 'levantamento' | 'documento' | 'planilha' | 'pacote' | 'imagem';

export interface FileKindMeta {
  readonly label: string;
  /** Uma frase sobre o que cai aqui, para a tela não precisar explicar. */
  readonly hint: string;
}

export const FILE_KIND_META: Record<FileKind, FileKindMeta> = {
  /*
   * A descrição diz o formato, não o assunto.
   *
   * Antes listava exemplos — "contratos, ART/RRT, licenças" —, o que sugeria
   * que a seção só aceitava aquilo. Ela aceita qualquer anexo da obra naquele
   * formato, e é isso que a frase precisa dizer.
   */
  projeto: {
    label: 'Projetos e modelos',
    hint: 'Anexos em formato de desenho, modelo BIM ou geometria 3D.',
  },
  levantamento: {
    label: 'Levantamentos',
    hint: 'Anexos em formato de nuvem de pontos.',
  },
  documento: {
    label: 'Documentos',
    hint: 'Anexos relacionados à obra.',
  },
  planilha: {
    label: 'Planilhas',
    hint: 'Anexos em formato de planilha.',
  },
  pacote: {
    label: 'Pacotes',
    hint: 'Anexos compactados, com vários arquivos dentro.',
  },
  imagem: {
    label: 'Imagens',
    hint: 'Anexos em imagem. As fotos de vistoria ficam na galeria.',
  },
};

/** A ordem em que as seções aparecem na tela. */
export const FILE_KIND_ORDER: readonly FileKind[] = [
  'documento',
  'planilha',
  'projeto',
  'levantamento',
  'pacote',
  'imagem',
];

const KIND_BY_EXTENSION = new Map<string, FileKind>([
  ...(
    [
      'dwg',
      'dxf',
      'rvt',
      'rfa',
      'rte',
      'skp',
      'nwd',
      'nwc',
      'nwf',
      'ifc',
      'dgn',
      '3dm',
      'pln',
      'sldprt',
      'sldasm',
      'slddrw',
      'ipt',
      'iam',
      'idw',
      'step',
      'stp',
      'iges',
      'igs',
      'stl',
      'obj',
      'fbx',
    ] as const
  ).map((extensao) => [extensao, 'projeto'] as const),
  ...(['las', 'laz'] as const).map((extensao) => [extensao, 'levantamento'] as const),
  ...(['pdf', 'doc', 'docx', 'txt', 'rtf', 'ppt', 'pptx'] as const).map(
    (extensao) => [extensao, 'documento'] as const,
  ),
  ...(['xlsx', 'xls', 'csv'] as const).map((extensao) => [extensao, 'planilha'] as const),
  ...(['zip', 'rar', '7z', 'tar', 'gz', 'tgz'] as const).map(
    (extensao) => [extensao, 'pacote'] as const,
  ),
  ...(['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'tif', 'tiff'] as const).map(
    (extensao) => [extensao, 'imagem'] as const,
  ),
]);

/**
 * A extensão de um nome de arquivo, minúscula e sem ponto.
 *
 * Nome sem ponto, ou terminado em ponto, devolve string vazia — que nunca está
 * em lista nenhuma e portanto é recusado.
 */
export function extensionOf(fileName: string): string {
  const partes = fileName.trim().toLowerCase().split('.');
  if (partes.length < 2) return '';
  return (partes.pop() ?? '').replace(/[^a-z0-9]/g, '');
}

/** Extensão desconhecida cai em "documento", que é o balaio honesto. */
export function fileKindOf(extension: string): FileKind {
  return KIND_BY_EXTENSION.get(extension.toLowerCase()) ?? 'documento';
}

// ─────────────────────────────────────────────────────── a política

export type UploadPolicyMode = 'padrao' | 'lista_branca';

export interface UploadPolicy {
  /**
   * `padrao` parte da lista de fábrica e aceita ajustes; `lista_branca`
   * substitui a lista inteira pelo que o órgão escreveu.
   */
  readonly mode: UploadPolicyMode;
  /** No modo padrão, o que se acrescenta. Na lista branca, a lista inteira. */
  readonly extras: readonly string[];
  /** No modo padrão, o que se retira da lista de fábrica. */
  readonly blocked: readonly string[];
  readonly maxBytes: number;
}

/** Limites que o órgão pode escolher para o tamanho de um arquivo. */
export const MIN_UPLOAD_LIMIT_BYTES = 1 * 1024 * 1024;

/**
 * Teto absoluto, acima do que qualquer órgão pode configurar.
 *
 * O servidor lê o arquivo inteiro na memória para calcular o SHA-256 e conferir
 * os bytes iniciais. Cem megabytes por requisição já é generoso para um modelo
 * BIM; mais do que isso, com alguns envios simultâneos, derruba o contêiner.
 */
export const MAX_UPLOAD_LIMIT_BYTES = 100 * 1024 * 1024;

export const DEFAULT_UPLOAD_LIMIT_BYTES = 20 * 1024 * 1024;

/**
 * Espaço em nuvem de uma organização, somando **tudo** que ela guarda: foto,
 * documento, projeto e vídeo. O vídeo mora no Bunny Stream e o resto no Bunny
 * Storage, mas quem paga a conta é a mesma organização, então o limite é um só.
 *
 * Cem gigabytes é o padrão porque é ordem de grandeza acima do que um município
 * do tamanho de Aripuanã consome: as 871 fotos migradas, com dois anos de obra,
 * somam menos de 300 MB. O número existe para conter o caso patológico — a
 * pasta de vídeos que alguém decide subir inteira —, não para racionar o uso
 * normal.
 */
export const DEFAULT_STORAGE_LIMIT_BYTES = 100 * 1024 * 1024 * 1024;

/** Piso do que se pode configurar: abaixo disso o órgão não opera. */
export const MIN_STORAGE_LIMIT_BYTES = 1024 * 1024 * 1024;

/** Teto do que se pode configurar, para um dedo escorregado não virar conta. */
export const MAX_STORAGE_LIMIT_BYTES = 10 * 1024 * 1024 * 1024 * 1024;

export const DEFAULT_UPLOAD_POLICY: UploadPolicy = {
  mode: 'padrao',
  extras: [],
  blocked: [],
  maxBytes: DEFAULT_UPLOAD_LIMIT_BYTES,
};

const PROIBIDAS = new Set<string>(FORBIDDEN_UPLOAD_EXTENSIONS);

/**
 * O conjunto que vale, depois de aplicar a configuração do órgão.
 *
 * A ordem importa: o piso proibido é subtraído por último, então nem a lista
 * branca nem os acréscimos conseguem reintroduzi-lo. As imagens são somadas
 * depois disso, porque a galeria depende delas.
 */
export function resolveAllowedExtensions(policy: UploadPolicy): ReadonlySet<string> {
  const normalizar = (lista: readonly string[]): string[] =>
    lista.map((item) => item.trim().toLowerCase().replace(/^\./, '')).filter((item) => item !== '');

  const base =
    policy.mode === 'lista_branca'
      ? new Set(normalizar(policy.extras))
      : new Set<string>([...DEFAULT_UPLOAD_EXTENSIONS, ...normalizar(policy.extras)]);

  if (policy.mode === 'padrao') {
    for (const bloqueada of normalizar(policy.blocked)) base.delete(bloqueada);
  }

  for (const proibida of PROIBIDAS) base.delete(proibida);
  for (const sempre of ALWAYS_ALLOWED_EXTENSIONS) base.add(sempre);

  return base;
}

/** As extensões que o órgão aceita, agrupadas para a tela mostrar. */
export function groupExtensionsByKind(
  extensions: Iterable<string>,
): ReadonlyArray<{ kind: FileKind; extensions: string[] }> {
  const porTipo = new Map<FileKind, string[]>();

  for (const extensao of extensions) {
    const tipo = fileKindOf(extensao);
    porTipo.set(tipo, [...(porTipo.get(tipo) ?? []), extensao]);
  }

  return FILE_KIND_ORDER.filter((tipo) => porTipo.has(tipo)).map((kind) => ({
    kind,
    extensions: (porTipo.get(kind) ?? []).sort((a, b) => a.localeCompare(b)),
  }));
}
